import React from 'react';
import api from '../api';

/**
 * FinalFilesSection — shared between Creatives and Scheduling pages.
 *
 * Props:
 *   woId        {number}  — work order ID
 *   formats     {Array}   — list of output format objects/strings (from WO)
 *   isApproved  {bool}    — show only when creative is approved
 *   schedulerView {bool}  — hides Upload/Re-upload controls; highlights Download + Mark Received
 */
export default function FinalFilesSection({ woId, formats = [], isApproved, schedulerView = false }) {
  const [files,     setFiles]     = React.useState([]);
  const [uploading, setUploading] = React.useState({});
  const [loading,   setLoading]   = React.useState(false);
  const fileInputRefs = React.useRef({});

  const load = React.useCallback(async () => {
    if (!isApproved) return;
    setLoading(true);
    try {
      const res = await api.get(`/creatives/${woId}/final-files`);
      setFiles(res.data.files || []);
    } catch(e) { /* silent */ }
    finally { setLoading(false); }
  }, [woId, isApproved]);

  React.useEffect(() => { load(); }, [load]);

  if (!isApproved) return null;

  const fmtName = (f) => typeof f === 'object' ? (f.name || '') : f;
  const fmtSize = (f) => typeof f === 'object' ? (f.size || f.dimensions || '') : '';

  const latestForFormat = (formatName) =>
    files.filter(f => f.format_name === formatName).sort((a, b) => b.version - a.version)[0];

  const handleUpload = async (formatName, formatSize, file) => {
    setUploading(u => ({ ...u, [formatName]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('format_name', formatName);
      if (formatSize) fd.append('format_size', formatSize);
      await api.post(`/creatives/${woId}/final-files`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await load();
    } catch(e) {
      alert('Upload failed: ' + (e.response?.data?.message || e.message));
    } finally {
      setUploading(u => ({ ...u, [formatName]: false }));
    }
  };

  const handleReceive = async (fileId) => {
    try {
      await api.patch(`/creatives/${woId}/final-files/${fileId}/receive`);
      await load();
    } catch(e) { alert('Failed: ' + (e.response?.data?.message || e.message)); }
  };

  const totalFormats  = formats.length;
  const uploadedCount = formats.filter(f => latestForFormat(fmtName(f))).length;
  const receivedCount = formats.filter(f => latestForFormat(fmtName(f))?.scheduler_received_at).length;
  const allReceived   = totalFormats > 0 && receivedCount === totalFormats;
  const allUploaded   = totalFormats > 0 && uploadedCount === totalFormats;

  const headerBg    = allReceived ? '#22c47a' : '#e87830';
  const borderColor = allReceived ? '#22c47a' : '#e87830';
  const shadowColor = allReceived ? 'rgba(34,196,122,0.15)' : 'rgba(232,120,48,0.15)';

  return (
    <div style={{
      marginTop: 20, borderRadius: 12, overflow: 'hidden',
      border: `2px solid ${borderColor}`,
      boxShadow: `0 2px 12px ${shadowColor}`,
    }}>
      {/* ── Header bar ── */}
      <div style={{
        padding: '10px 16px', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', background: headerBg,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: '1.1rem' }}>📦</span>
          <span style={{ fontSize: '0.8rem', fontWeight: 800, color: '#fff', letterSpacing: '0.05em' }}>
            HI-RES FINAL FILES
          </span>
          <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
            {schedulerView ? '— download & confirm receipt' : '— for scheduler handoff'}
          </span>
          {loading && <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.8)' }}>⏳</span>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            fontSize: '0.68rem', fontWeight: 700,
            background: 'rgba(255,255,255,0.25)', color: '#fff',
            padding: '2px 8px', borderRadius: 20,
          }}>
            {receivedCount}/{totalFormats} received
          </span>
          {!allUploaded && (
            <span style={{
              fontSize: '0.68rem', fontWeight: 700,
              background: 'rgba(255,255,255,0.2)', color: '#fff',
              padding: '2px 8px', borderRadius: 20,
            }}>
              ⚠ {totalFormats - uploadedCount} pending upload
            </span>
          )}
          {allReceived && (
            <span style={{
              fontSize: '0.68rem', fontWeight: 700,
              background: 'rgba(255,255,255,0.2)', color: '#fff',
              padding: '2px 8px', borderRadius: 20,
            }}>
              ✓ All received
            </span>
          )}
        </div>
      </div>

      {/* ── Rows ── */}
      <div style={{ background: 'var(--bg1)', padding: '8px 12px' }}>
        {formats.length === 0 && (
          <div style={{ fontSize: '0.72rem', color: 'var(--text3)', padding: '8px 4px' }}>
            No output formats assigned.
          </div>
        )}
        {formats.map((fmt, i) => {
          const name   = fmtName(fmt);
          const size   = fmtSize(fmt);
          const latest = latestForFormat(name);
          const busy   = uploading[name];
          const isRcvd = !!latest?.scheduler_received_at;
          const dotColor = isRcvd ? '#22c47a' : latest ? '#e87830' : '#9ca3af';

          return (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '10px 12px',
              marginBottom: i < formats.length - 1 ? 6 : 0,
              background: isRcvd ? 'rgba(34,196,122,0.06)' : latest ? 'rgba(232,120,48,0.06)' : 'var(--bg2)',
              border: `1px solid ${isRcvd ? 'rgba(34,196,122,0.2)' : latest ? 'rgba(232,120,48,0.2)' : 'var(--border)'}`,
              borderRadius: 8, opacity: busy ? 0.7 : 1,
            }}>
              {/* Status dot */}
              <div style={{
                width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                background: dotColor, boxShadow: `0 0 0 3px ${dotColor}22`,
              }} title={isRcvd ? 'Received' : latest ? 'Awaiting receipt' : 'Not uploaded'} />

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text)' }}>
                  {name}
                  {size && <span style={{ color: 'var(--text3)', fontWeight: 400, marginLeft: 4 }}>{size}</span>}
                </div>
                {latest ? (
                  <div style={{
                    fontSize: '0.65rem', color: 'var(--text3)', marginTop: 2,
                    display: 'flex', flexWrap: 'wrap', gap: '0 6px',
                  }}>
                    <span style={{ color: 'var(--text2)', fontWeight: 600 }}>{latest.file_name}</span>
                    <span>
                      {Math.round(latest.file_size_kb)}KB · v{latest.version} · {latest.uploaded_by} ·{' '}
                      {latest.uploaded_at ? new Date(latest.uploaded_at).toLocaleString() : ''}
                    </span>
                    {isRcvd && (
                      <span style={{ color: '#22c47a', fontWeight: 700 }}>
                        ✓ Rcvd by {latest.received_by} · {new Date(latest.scheduler_received_at).toLocaleString()}
                      </span>
                    )}
                  </div>
                ) : (
                  <div style={{ fontSize: '0.65rem', color: '#9ca3af', marginTop: 2, fontStyle: 'italic' }}>
                    Not uploaded yet
                  </div>
                )}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
                {/* Upload — hidden in scheduler view */}
                {!schedulerView && (
                  <>
                    <input type="file" ref={el => fileInputRefs.current[name] = el} style={{ display: 'none' }}
                      onChange={e => { if (e.target.files[0]) handleUpload(name, size, e.target.files[0]); e.target.value = ''; }} />
                    <button
                      onClick={() => fileInputRefs.current[name]?.click()}
                      disabled={busy}
                      style={{
                        padding: '4px 10px', fontSize: '0.7rem', fontWeight: 700,
                        background: latest ? 'transparent' : '#e87830',
                        color: latest ? '#e87830' : '#fff',
                        border: '1px solid #e87830', borderRadius: 6,
                        cursor: 'pointer', whiteSpace: 'nowrap',
                      }}>
                      {busy ? '⏳' : latest ? '↑ Re-upload' : '↑ Upload'}
                    </button>
                  </>
                )}

                {/* Download */}
                {latest && (
                  <a href={latest.url} download={latest.file_name} target="_blank" rel="noreferrer"
                    style={{
                      padding: '4px 10px', fontSize: '0.7rem', fontWeight: 700,
                      background: schedulerView && !isRcvd ? '#6c58f0' : 'transparent',
                      color: schedulerView && !isRcvd ? '#fff' : '#6c58f0',
                      border: '1px solid #6c58f0', borderRadius: 6,
                      textDecoration: 'none', whiteSpace: 'nowrap',
                    }}>
                    ↓ Download
                  </a>
                )}

                {/* Mark Received — scheduler only */}
                {schedulerView && latest && !isRcvd && (
                  <button onClick={() => handleReceive(latest.id)}
                    style={{
                      padding: '4px 10px', fontSize: '0.7rem', fontWeight: 700,
                      background: '#22c47a', color: '#fff',
                      border: '1px solid #22c47a', borderRadius: 6,
                      cursor: 'pointer', whiteSpace: 'nowrap',
                    }}>
                    ✓ Mark Received
                  </button>
                )}

                {/* Already received badge */}
                {isRcvd && (
                  <span style={{
                    fontSize: '0.7rem', fontWeight: 700, color: '#22c47a',
                    padding: '4px 10px', border: '1px solid rgba(34,196,122,0.3)',
                    borderRadius: 6, background: 'rgba(34,196,122,0.08)',
                  }}>
                    ✓ Received
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}